import React, { useCallback, useEffect, useState } from 'react'
import { App, Button, Form, Input, Modal, Select, Space, Table, Tag } from 'antd'
import type { TableColumnsType } from 'antd'
import { DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../../../components/common/PaginatedListTable'
import { useDedupedMessage } from '../../../hooks/useDedupedMessage'
import {
    createManualRecognitionType,
    deleteManualRecognitionType,
    getManualRecognitionModelTypeBindings,
    getManualRecognitionTypes,
    getModelRawClassOptions,
    replaceManualRecognitionModelTypeBindings,
    updateManualRecognitionType,
} from '../../../services/modelManagementService'
import type {
    ManualRecognitionModelTypeBindingSummary,
    ManualRecognitionTypeSummary,
    ModelRawClassOptionSummary,
} from '../../../types/modelManagement'
import { getRawClassLocalizedName } from '../../../utils/rawClassDisplay'
import {
    getModelManagementErrorDetail,
    MODEL_MANAGEMENT_I18N_PREFIX,
} from './pageConfig'
import NonDismissiblePopconfirm from '../../../components/common/NonDismissiblePopconfirm'
import TypeColorSelect from '../../../components/common/TypeColorSelect'
import { RESERVED_TYPE_COLOR } from '../../../components/common/typeColorSelectConfig'

interface ManualTypeFormValues {
    displayNameZhCn?: string
    color?: string
}

interface ManualRecognitionTypesSectionProps {
    canUpdate: boolean
}

const MANUAL_TYPE_COLOR_PALETTE = [
    '#1677ff',
    '#52c41a',
    '#13c2c2',
    '#722ed1',
    '#fa8c16',
    '#eb2f96',
    '#2f54eb',
    '#a0d911',
    '#fa541c',
    '#08979c',
]

const ManualRecognitionTypesSection: React.FC<
    ManualRecognitionTypesSectionProps
> = ({ canUpdate }) => {
    const { message: rawMessage, modal } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { i18n, t } = useTranslation(['admin'])
    const [form] = Form.useForm<ManualTypeFormValues>()
    const [manualTypes, setManualTypes] = useState<ManualRecognitionTypeSummary[]>([])
    const [modelTypes, setModelTypes] = useState<ModelRawClassOptionSummary[]>([])
    const [modelTypeBindings, setModelTypeBindings] = useState<
        ManualRecognitionModelTypeBindingSummary[]
    >([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [actionKey, setActionKey] = useState<string | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingType, setEditingType] =
        useState<ManualRecognitionTypeSummary | null>(null)
    const [bindingType, setBindingType] =
        useState<ManualRecognitionTypeSummary | null>(null)
    const [bindingValues, setBindingValues] = useState<string[]>([])

    const localizedName = useCallback(
        (record: ManualRecognitionTypeSummary) => {
            const language = i18n.language.toLowerCase()
            if (language.startsWith('en')) return record.display_name_en
            if (language.includes('hk') || language.includes('hant')) {
                return record.display_name_zh_hk
            }
            return record.display_name_zh_cn
        },
        [i18n.language]
    )

    const formatModelTypeName = useCallback(
        (record: ModelRawClassOptionSummary) => {
            if (record.display_name_zh) {
                return `${record.display_name_zh} (${record.raw_class})`
            }
            const localized = getRawClassLocalizedName(record.raw_class, t)
            return localized === record.raw_class
                ? record.raw_class
                : `${localized} (${record.raw_class})`
        },
        [t]
    )

    const loadTypes = useCallback(async () => {
        setLoading(true)
        try {
            const [nextManualTypes, nextRawClassOptions, nextBindings] =
                await Promise.all([
                    getManualRecognitionTypes(),
                    getModelRawClassOptions('default'),
                    getManualRecognitionModelTypeBindings(),
                ])
            setManualTypes(nextManualTypes)
            setModelTypes(nextRawClassOptions)
            setModelTypeBindings(nextBindings)
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.loadManualTypesFailed`),
                    t
                )
            )
        } finally {
            setLoading(false)
        }
    }, [message, t])

    useEffect(() => {
        void loadTypes()
    }, [loadTypes])

    const openTypeModal = (manualType?: ManualRecognitionTypeSummary) => {
        setEditingType(manualType ?? null)
        const usedColors = new Set(manualTypes.map((item) => item.color.toLowerCase()))
        const nextColor =
            MANUAL_TYPE_COLOR_PALETTE.find(
                (color) => !usedColors.has(color.toLowerCase())
            ) ?? '#722ed1'
        form.resetFields()
        form.setFieldsValue({
            displayNameZhCn: manualType?.display_name_zh_cn,
            color: manualType?.color ?? nextColor,
        })
        setModalOpen(true)
    }

    const closeTypeModal = () => {
        setModalOpen(false)
        setEditingType(null)
    }

    const openBindingModal = (manualType: ManualRecognitionTypeSummary) => {
        setBindingType(manualType)
        setBindingValues(
            modelTypeBindings
                .filter((binding) => binding.manual_type_key === manualType.key)
                .map((binding) => binding.raw_class_norm)
        )
    }

    const closeBindingModal = () => {
        setBindingType(null)
        setBindingValues([])
    }

    const saveBindings = async () => {
        if (!bindingType) return
        setSaving(true)
        try {
            await replaceManualRecognitionModelTypeBindings(bindingType.key, {
                rawClassNorms: bindingValues,
            })
            closeBindingModal()
            await loadTypes()
            message.success(
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeBindingSaveSuccess`)
            )
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeBindingSaveFailed`),
                    t
                )
            )
        } finally {
            setSaving(false)
        }
    }

    const saveType = async () => {
        const values = await form.validateFields()
        if (!values.displayNameZhCn || !values.color) return
        setSaving(true)
        try {
            if (editingType) {
                await updateManualRecognitionType(editingType.key, {
                    displayNameZhCn: values.displayNameZhCn.trim(),
                    color: values.color.toLowerCase(),
                })
            } else {
                await createManualRecognitionType({
                    displayNameZhCn: values.displayNameZhCn.trim(),
                    color: values.color.toLowerCase(),
                })
            }
            closeTypeModal()
            await loadTypes()
            message.success(
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeSaveSuccess`)
            )
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeSaveFailed`),
                    t
                )
            )
        } finally {
            setSaving(false)
        }
    }

    const deleteType = async (manualType: ManualRecognitionTypeSummary) => {
        setActionKey(manualType.key)
        try {
            await deleteManualRecognitionType(manualType.key)
            await loadTypes()
            message.success(
                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeDeleteSuccess`)
            )
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeDeleteFailed`),
                    t
                )
            )
        } finally {
            setActionKey(null)
        }
    }

    const columns: TableColumnsType<ManualRecognitionTypeSummary> = [
            {
                title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.columns.name`),
                key: 'name',
                width: 180,
                ellipsis: { showTitle: false },
                render: (_value, record) =>
                    renderPaginatedListText(localizedName(record)),
            },
            {
                title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.columns.key`),
                dataIndex: 'key',
                key: 'key',
                width: 200,
                render: renderPaginatedListText,
            },
            {
                title: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.columns.color`),
                dataIndex: 'color',
                key: 'color',
                width: 100,
                render: (color: string) => (
                    <span
                        aria-label={color}
                        style={{
                            background: color,
                            border: '1px solid rgba(0, 0, 0, 0.2)',
                            borderRadius: 4,
                            display: 'inline-block',
                            height: 20,
                            verticalAlign: 'middle',
                            width: 32,
                        }}
                    />
                ),
            },
            {
                title: t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.columns.modelTypes`
                ),
                key: 'modelTypes',
                width: 260,
                render: (_value, record) => {
                    const bindings = modelTypeBindings.filter(
                        (binding) => binding.manual_type_key === record.key
                    )
                    return bindings.length ? (
                        <Space
                            size={[4, 4]}
                            style={{ maxWidth: '100%', minWidth: 0 }}
                            wrap
                        >
                            {bindings.map((binding) => {
                                const modelType = modelTypes.find(
                                    (item) =>
                                        item.raw_class_norm ===
                                            binding.raw_class_norm
                                )
                                const label = modelType
                                    ? formatModelTypeName(modelType)
                                    : binding.raw_class_norm
                                return (
                                    <Tag
                                        key={binding.raw_class_norm}
                                        style={{ maxWidth: '100%' }}
                                    >
                                        {renderPaginatedListText(label)}
                                    </Tag>
                                )
                            })}
                        </Space>
                    ) : (
                        renderPaginatedListText(
                            t(`${MODEL_MANAGEMENT_I18N_PREFIX}.empty.noBindings`)
                        )
                    )
                },
            },
            {
                title: t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.columns.actions`
                ),
                key: 'actions',
                fixed: 'right',
                width: 260,
                render: (_value, record) => {
                    if (!canUpdate) return null
                    const deleteBlockedMessage = record.deletion_block_reason
                        ? t(
                              `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeDeleteUsedBlocked`
                          )
                        : undefined
                    return (
                        <Space size={4} wrap>
                            <Button
                                icon={<LinkOutlined />}
                                onClick={() => openBindingModal(record)}
                                type="link"
                            >
                                {t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.bindModelTypes`
                                )}
                            </Button>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => openTypeModal(record)}
                                type="link"
                            >
                                {t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.editManualType`
                                )}
                            </Button>
                            {record.can_delete ? (
                                <NonDismissiblePopconfirm
                                    title={t(
                                        `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.confirmDeleteManualType`
                                    )}
                                    onConfirm={() => void deleteType(record)}
                                >
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={actionKey === record.key}
                                        type="link"
                                    >
                                        {t(
                                            `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.deleteManualType`
                                        )}
                                    </Button>
                                </NonDismissiblePopconfirm>
                            ) : (
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                        modal.warning({
                                            title: t(
                                                `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeDeleteBlockedTitle`
                                            ),
                                            content: deleteBlockedMessage,
                                            maskClosable: false,
                                        })
                                    }
                                    type="link"
                                >
                                    {t(
                                        `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.deleteManualType`
                                    )}
                                </Button>
                            )}
                        </Space>
                    )
                },
            },
        ]

    return (
        <section>
            <PaginatedListContainer>
                <PaginatedListToolbar>
                    {canUpdate ? (
                        <Button
                            icon={<PlusOutlined />}
                            onClick={() => openTypeModal()}
                            type="primary"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.createManualType`
                            )}
                        </Button>
                    ) : null}
                </PaginatedListToolbar>
                <PaginatedListCard>
                    <Table
                        columns={columns}
                        dataSource={manualTypes}
                        loading={loading}
                        pagination={buildPaginatedListPagination({
                            defaultPageSize: 10,
                            showTotal: (total) =>
                                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                                    total,
                                }),
                        })}
                        rowKey="key"
                        scroll={{ x: 1220 }}
                        size={PAGINATED_LIST_TABLE_SIZE}
                    />
                </PaginatedListCard>
            </PaginatedListContainer>

            <Modal
                confirmLoading={saving}
                destroyOnClose
                maskClosable={false}
                okText={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.submitBinding`
                )}
                onCancel={closeBindingModal}
                onOk={() => void saveBindings()}
                open={Boolean(bindingType)}
                title={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.bindingTitle`,
                    { name: bindingType ? localizedName(bindingType) : '' }
                )}
            >
                <Select
                    allowClear
                    aria-label={t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.modelTypeBindingAriaLabel`
                    )}
                    mode="multiple"
                    onChange={setBindingValues}
                    options={modelTypes.map((modelType) => {
                        const value = modelType.raw_class_norm
                        const occupiedBinding = modelTypeBindings.find(
                            (binding) =>
                                binding.raw_class_norm ===
                                    modelType.raw_class_norm &&
                                binding.manual_type_key !== bindingType?.key
                        )
                        return {
                            disabled: Boolean(occupiedBinding),
                            label: `${formatModelTypeName(modelType)}${
                                occupiedBinding
                                    ? ` - ${t(`${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.boundElsewhere`)}`
                                    : ''
                            }`,
                            value,
                        }
                    })}
                    placeholder={t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypes.modelTypeBindingPlaceholder`
                    )}
                    popupMatchSelectWidth={false}
                    showSearch
                    style={{ width: '100%' }}
                    value={bindingValues}
                />
            </Modal>

            <Modal
                confirmLoading={saving}
                destroyOnClose
                forceRender
                okText={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.submitManualType`
                )}
                onCancel={closeTypeModal}
                onOk={() => void saveType()}
                open={modalOpen}
                maskClosable={false}
                title={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.manualTypeModal.${editingType ? 'editTitle' : 'createTitle'}`
                )}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.manualTypeName`
                        )}
                        name="displayNameZhCn"
                        rules={[
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeNameRequired`
                                ),
                                required: true,
                                whitespace: true,
                            },
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.manualTypeNameTooLong`
                                ),
                                max: 64,
                            },
                        ]}
                    >
                        <Input maxLength={64} />
                    </Form.Item>
                    <Form.Item
                        label={t(
                            `${MODEL_MANAGEMENT_I18N_PREFIX}.form.manualTypeColor`
                        )}
                        name="color"
                        rules={[
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorRequired`
                                ),
                                required: true,
                            },
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorInvalid`
                                ),
                                pattern: /^#[0-9A-Fa-f]{6}$/,
                            },
                            {
                                message: t(
                                    `${MODEL_MANAGEMENT_I18N_PREFIX}.messages.colorReserved`
                                ),
                                validator: (_rule, value) =>
                                    value?.toLowerCase() === RESERVED_TYPE_COLOR
                                        ? Promise.reject()
                                        : Promise.resolve(),
                            },
                        ]}
                    >
                        <TypeColorSelect
                            aria-label={t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.form.manualTypeColor`
                            )}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </section>
    )
}

export default ManualRecognitionTypesSection
